import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Flex, Group, Select, Text, UnstyledButton } from "@mantine/core";
import type { DatesRangeValue } from "@mantine/dates";
import { Badge, DateRangeFilter } from "@onengine/component-library";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { z } from "zod";

import {
  useAlertsQuery,
  useResolveAlertMutation,
} from "@/api/live-dashboard/live-dashboard.queries";
import type {
  LiveDashboardAlert,
  LiveDashboardAlertStatusFilter,
  LiveDashboardAlertType,
  ResolveAlertAction,
} from "@/api/live-dashboard/live-dashboard.types";
import type { RouterContextType } from "@/api/permissions/permissions.api";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import Table from "@/components/ui/Table/Table";
import TableActionsMenu from "@/components/ui/TableActionsMenu/TableActionsMenu";
import type { TColumn } from "@/components/ui/TableCard/TableCard";
import type { AlertCreatedEventData } from "@/hooks/live-dashboard/useLiveDashboardChannel";
import { useLiveDashboardChannel } from "@/hooks/live-dashboard/useLiveDashboardChannel";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { tableSearchSchema, useTableUrlPersistence } from "@/hooks/useTableUrlPersistence";
import { authGuardWithPermissions } from "@/utils/authGuard";
import {
  getLiveDashboardAlertStatusBadgeProps,
  getLiveDashboardAlertTypeBadgeProps,
  getServiceProviderLockoutBadgeProps,
} from "@/utils/badgeConfig";
import { formatToDateTimeString, toValidDate } from "@/utils/dateFormatter";
import { getRouteIcon } from "@/utils/getRouteIconComponent";
import { showAlert } from "@/utils/responseHandler";

import { LiveMapAlertResolveModal } from "@/components/features/LiveMap/LiveMapAlertResolveModal";
import { LiveMapTabs } from "@/components/features/LiveMap/LiveMapTabs";
import {
  LIVE_MAP_ALERT_FILTER_ALL_VALUE,
  LIVE_MAP_ALERT_STATUS_OPTIONS,
  LIVE_MAP_ALERT_TYPE_FILTER_OPTIONS,
} from "@/components/features/LiveMap/liveMap.constants";
import { renderAlertDetail } from "@/components/features/LiveMap/liveMap.utils";
import styles from "@/components/features/LiveMap/live-map.module.scss";

const alertsSearchSchema = tableSearchSchema.extend({
  // Deep-link target from a bell notification (location_anomaly category, SERV-3109) or
  // this page's own realtime "View" toast action - see handleAlertCreated below.
  highlight: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/_authenticated/live-map/alerts")({
  component: LiveMapAlerts,
  validateSearch: alertsSearchSchema.parse,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

const DEFAULT_TABLE_CARD_PROPS = {
  // Filters live in the always-visible bar above (status/type/SP/date), per spec 9.7 G -
  // the chip-based filter drawer TableCard also offers isn't used, so its button is hidden.
  showFiltersButton: false,
  showTableSettingsButton: false,
};

const INITIAL_TABLE_COLUMNS: TColumn[] = [
  { id: "createdAt", label: "Time", visible: true },
  { id: "serviceProvider", label: "Service Provider", visible: true },
  { id: "type", label: "Alert", visible: true },
  { id: "workOrder", label: "WO / Visit", visible: true },
  { id: "detail", label: "Details", visible: true },
  { id: "status", label: "Status", visible: true },
  { id: "actions", label: "", visible: true },
];

/** Same shape as FmIntegrations/LogsTab.tsx's own rangeBound - full-day bounds in local time,
 * converted to UTC ISO for the `from`/`to` query params (9.6). */
function rangeBound(value: DatesRangeValue[number], endOfDay: boolean): string | undefined {
  const date = toValidDate(value as Date | string | null);
  if (!date) return undefined;
  if (endOfDay) {
    date.setHours(23, 59, 59, 999);
  } else {
    date.setHours(0, 0, 0, 0);
  }
  return date.toISOString();
}

function LiveMapAlerts() {
  useBreadcrumb([
    {
      title: "Live Map",
      href: "/live-map",
      icon: getRouteIcon(MODULE_KEYS_MAP.dispatchQueue),
    },
  ]);

  const navigate = useNavigate();
  const searchParams = useSearch({ from: "/_authenticated/live-map/alerts" });
  const [tableColumns, setTableColumns] = useState<TColumn[]>(INITIAL_TABLE_COLUMNS);

  const { page, setPage, pageSize, setPageSize } = useTableUrlPersistence({
    viewName: "liveMapAlertsList",
    filterKeyToLabel: {},
    searchParams,
    navigate,
    tableColumns,
  });

  const [statusFilter, setStatusFilter] = useState<LiveDashboardAlertStatusFilter>("open");
  const [typeFilter, setTypeFilter] = useState<LiveDashboardAlertType | "">("");
  const [serviceProviderFilter, setServiceProviderFilter] = useState("");
  const [dateRange, setDateRange] = useState<DatesRangeValue>([null, null]);

  // Reset to page 1 whenever a filter narrows/widens the result set, so the user never
  // lands on a now out-of-range page (same rule dispatch-queue's own filters follow).
  useEffect(() => {
    setPage(1);
  }, [statusFilter, typeFilter, serviceProviderFilter, dateRange, setPage]);

  const filters = useMemo(
    () => ({
      status: statusFilter,
      type: typeFilter || undefined,
      serviceProviderId: serviceProviderFilter || undefined,
      from: rangeBound(dateRange[0], false),
      to: rangeBound(dateRange[1], true),
      page,
      pageSize,
    }),
    [statusFilter, typeFilter, serviceProviderFilter, dateRange, page, pageSize],
  );

  const alertsQuery = useAlertsQuery(filters);
  const alerts = useMemo(() => alertsQuery.data?.items ?? [], [alertsQuery.data]);
  const totalRecords = alertsQuery.data?.total ?? 0;

  // No dedicated SP-search endpoint is documented for this filter (9.6 only takes a
  // serviceProviderId, never a picklist source) - rather than invent one, the SP Select's
  // options accumulate from every service provider seen across alert responses so far, so
  // the list doesn't shrink to one name once a selection narrows the visible rows.
  const [knownServiceProviders, setKnownServiceProviders] = useState<Map<string, string>>(
    () => new Map(),
  );
  useEffect(() => {
    if (!alertsQuery.data) return;
    const items = alertsQuery.data.items;
    setKnownServiceProviders((previous) => {
      let changed = false;
      const next = new Map(previous);
      for (const item of items) {
        if (!next.has(item.serviceProvider.id)) {
          next.set(item.serviceProvider.id, item.serviceProvider.name);
          changed = true;
        }
      }
      return changed ? next : previous;
    });
  }, [alertsQuery.data]);

  const serviceProviderOptions = useMemo(
    () =>
      Array.from(knownServiceProviders.entries())
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [knownServiceProviders],
  );

  const resolveMutation = useResolveAlertMutation();
  const [alertBeingResolved, setAlertBeingResolved] = useState<LiveDashboardAlert | null>(null);

  const handleResolveSubmit = (note: string | undefined, action: ResolveAlertAction) => {
    if (!alertBeingResolved) return;
    resolveMutation.mutate(
      { alertId: alertBeingResolved.id, note, action },
      { onSuccess: () => setAlertBeingResolved(null) },
    );
  };

  // ── Deep-link highlight (?highlight=alertId): scroll to + flash the matching row once it
  // appears in the loaded page, from a bell notification or this page's own realtime toast.
  const highlightedAlertId = searchParams.highlight ?? null;
  const highlightedRowRef = useRef<HTMLSpanElement | null>(null);
  const hasScrolledToHighlightRef = useRef(false);

  useEffect(() => {
    hasScrolledToHighlightRef.current = false;
  }, [highlightedAlertId]);

  useEffect(() => {
    if (!highlightedAlertId || hasScrolledToHighlightRef.current) return;
    if (!alerts.some((alert) => alert.id === highlightedAlertId)) return;
    highlightedRowRef.current?.scrollIntoView({ block: "center" });
    hasScrolledToHighlightRef.current = true;
  }, [highlightedAlertId, alerts]);

  // ── Realtime: toast + list refresh on alert.created (spec 9.7 G). The list refresh
  // itself is handled by useLiveDashboardChannel's own debounced invalidate (unchanged);
  // this callback only adds the toast + "View" deep-link on top of it.
  const handleAlertCreated = (event: AlertCreatedEventData) => {
    showAlert({
      // Review finding (SERV-3138): without an explicit id, showAlert's own default
      // (`alert-${title}-${message}`) is identical for every alert.created event, since
      // title/message are static literals here -- Mantine's notification store no-ops a
      // show() for an id already in the store, so a second alert.created while the
      // first toast is still up was silently dropped entirely. Keying on the real
      // alertId lets concurrent alerts each get their own toast.
      id: `live-map-alert-created-${event.alertId}`,
      title: "New location alert",
      message: "A new alert was just raised for a service provider on a live visit.",
      type: "warning",
      // Longer than the 3s default (review finding): this toast carries the only
      // one-click path to the new alert, and 3s isn't enough time to notice it, read
      // it, and click "View".
      autoClose: 8000,
      actions: [
        {
          label: "View",
          onClick: () =>
            navigate({
              to: "/live-map/alerts",
              search: (previous) => ({ ...previous, highlight: event.alertId }),
            }),
        },
      ],
    });
  };
  useLiveDashboardChannel({ onAlertCreated: handleAlertCreated });

  // useCallback (review finding, SERV-3138): a fresh inline arrow here is in
  // Table.tsx's own useMemo dependency array for the whole <Thead>/<Tbody>, so a new
  // identity every render invalidated that memo and recomputed the entire table body
  // on every re-render of this page, including every realtime alert.created/resolved
  // event and every filter keystroke.
  const getRowClassName = useCallback(
    (alert: LiveDashboardAlert) =>
      alert.id === highlightedAlertId ? styles.alertRowHighlighted : undefined,
    [highlightedAlertId],
  );

  const columns = useMemo<ColumnDef<LiveDashboardAlert, unknown>[]>(
    () => [
      {
        accessorKey: "createdAt",
        header: "Time",
        cell: ({ row }) => {
          const alert = row.original;
          const isHighlighted = alert.id === highlightedAlertId;
          return (
            <Text
              size="sm"
              component="span"
              ref={isHighlighted ? highlightedRowRef : undefined}
              data-highlighted={isHighlighted || undefined}
            >
              {formatToDateTimeString(alert.createdAt)}
            </Text>
          );
        },
      },
      {
        accessorKey: "serviceProvider",
        header: "Service Provider",
        cell: ({ row }) => {
          const alert = row.original;
          const lockoutBadge = getServiceProviderLockoutBadgeProps(
            alert.serviceProviderBlocked,
            alert.serviceProviderBlacklisted,
          );
          return (
            <Group gap={4} wrap="nowrap">
              <Text size="sm">{alert.serviceProvider.name}</Text>
              {lockoutBadge && (
                <Badge size="small" color={lockoutBadge.color} variant={lockoutBadge.variant}>
                  {lockoutBadge.children}
                </Badge>
              )}
            </Group>
          );
        },
      },
      {
        accessorKey: "type",
        header: "Alert",
        cell: ({ row }) => {
          const badge = getLiveDashboardAlertTypeBadgeProps(row.original.type);
          return (
            <Badge size="small" color={badge.color} variant={badge.variant}>
              {badge.children}
            </Badge>
          );
        },
      },
      {
        accessorKey: "workOrder",
        header: "WO / Visit",
        cell: ({ row }) => {
          const alert = row.original;
          return (
            <Group gap={4} wrap="nowrap">
              <UnstyledButton
                type="button"
                className={styles.alertWorkOrderLinkButton}
                onClick={() =>
                  navigate({
                    to: "/work-orders/$workOrderId",
                    params: { workOrderId: alert.workOrder.id },
                  })
                }
              >
                <Text size="sm" className={styles.alertWorkOrderLink}>
                  {alert.workOrder.workOrderNumber}
                </Text>
              </UnstyledButton>
              <Text size="sm" c="dimmed">
                {alert.visit.visitLabel}
              </Text>
            </Group>
          );
        },
      },
      {
        accessorKey: "detail",
        header: "Details",
        cell: ({ row }) => <Text size="sm">{renderAlertDetail(row.original)}</Text>,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => {
          const alert = row.original;
          const badge = getLiveDashboardAlertStatusBadgeProps(alert.resolvedAt);
          return (
            <div className={styles.alertsStatusBadgeCell}>
              <Badge size="small" color={badge.color} variant={badge.variant}>
                {badge.children}
              </Badge>
              {alert.resolvedAt && (
                <Text size="xs" c="dimmed">
                  {formatToDateTimeString(alert.resolvedAt)}
                </Text>
              )}
              {alert.resolutionNote && (
                <Text size="xs" c="dimmed" truncate className={styles.alertResolutionNote}>
                  {alert.resolutionNote}
                </Text>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "actions",
        header: "",
        cell: ({ row }) => {
          const alert = row.original;
          if (alert.resolvedAt) {
            return (
              <Text size="sm" c="dimmed">
                —
              </Text>
            );
          }
          return (
            <TableActionsMenu
              dotOrientation="horizontal"
              options={[
                {
                  btnText: "Resolve",
                  onClickHandler: () => setAlertBeingResolved(alert),
                },
              ]}
            />
          );
        },
        enableSorting: false,
        enableHiding: false,
      },
    ],
    [highlightedAlertId, navigate],
  );

  return (
    <Flex direction="column" className={styles.container} gap="var(--spacing-medium)">
      <LiveMapTabs activeTab="alerts" />

      <Group gap="var(--spacing-medium)" className={styles.alertsFilterBar} wrap="wrap">
        <Select
          label="Alert status"
          data={LIVE_MAP_ALERT_STATUS_OPTIONS}
          value={statusFilter}
          onChange={(value) =>
            setStatusFilter((value as LiveDashboardAlertStatusFilter | null) ?? "open")
          }
          allowDeselect={false}
          w={160}
        />
        <Select
          label="Alert type"
          data={[
            { value: LIVE_MAP_ALERT_FILTER_ALL_VALUE, label: "All types" },
            ...LIVE_MAP_ALERT_TYPE_FILTER_OPTIONS,
          ]}
          value={typeFilter}
          onChange={(value) => setTypeFilter((value as LiveDashboardAlertType | "" | null) ?? "")}
          allowDeselect={false}
          w={240}
        />
        <Select
          label="Service provider"
          data={[
            { value: LIVE_MAP_ALERT_FILTER_ALL_VALUE, label: "All service providers" },
            ...serviceProviderOptions,
          ]}
          value={serviceProviderFilter}
          onChange={(value) => setServiceProviderFilter(value ?? "")}
          allowDeselect={false}
          searchable
          w={220}
        />
        <DateRangeFilter label="Date range" value={dateRange} onChange={setDateRange} />
      </Group>

      <Table
        data={alerts}
        columns={columns}
        enableSorting={false}
        manualPagination
        pageCount={Math.ceil(totalRecords / pageSize) || 1}
        pageIndex={page - 1}
        totalRecords={totalRecords}
        onPageChange={(newPageIndex) => setPage(newPageIndex + 1)}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          setPage(1);
        }}
        pageSize={pageSize}
        isLoading={alertsQuery.isPending}
        showTableCard
        getRowClassName={getRowClassName}
        columnPinning={{ right: ["actions"] }}
        tableCardProps={{
          ...DEFAULT_TABLE_CARD_PROPS,
          columns: tableColumns,
          onColumnsChange: setTableColumns,
          isLoading: alertsQuery.isPending,
        }}
      />

      <LiveMapAlertResolveModal
        alert={alertBeingResolved}
        isSubmitting={resolveMutation.isPending}
        onClose={() => setAlertBeingResolved(null)}
        onSubmit={handleResolveSubmit}
      />
    </Flex>
  );
}
